import { useMemo, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listPractitioners } from "@/api/practitioners";
import { getProfile } from "@/api/profile";
import {
  downloadRosterCsv,
  getRoster,
  type OrganizationKind,
  type RosterCell,
  type RosterGroup,
} from "@/api/roster";
import { PageHeader } from "@/components/PageHeader";
import { RosterMobileDay } from "@/components/RosterMobileDay";
import { SlotKindChip } from "@/components/SlotKindChip";
import type { Organization } from "@/api/types";
import { slotIsOperating } from "@/lib/operatingHours";

function rosterGroupToOrg(g: RosterGroup): Organization {
  return {
    operates_weekends: g.operates_weekends ?? true,
    operates_holidays: g.operates_holidays ?? true,
    operating_hours_start: g.operating_hours_start ?? null,
    operating_hours_end: g.operating_hours_end ?? null,
    closed_dates: g.closed_dates ?? [],
  } as unknown as Organization;
}

const KIND_ORDER: OrganizationKind[] = [
  "SAMU",
  "CRRA",
  "SMUR",
  "SAS",
  "SNP",
  "PDS",
  "CESU",
  "MMG",
  "SERVICE",
  "AUTRE",
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoPlus(iso: string, days: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function isGardeLabel(label: string): boolean {
  return / \((G|1\/2G)\)/i.test(label);
}

function formatDow(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

const MEMBERSHIP_KIND_COLOR_LIGHT: Record<string, string> = {
  SALARIE: "#1976d2",   // blue 700
  LIBERAL: "#7b1fa2",   // purple 700
  VACATAIRE: "#ef6c00", // orange 800
  STAGIAIRE: "#616161", // gray 700
  RESERVISTE: "#2e7d32", // green 800
};
const MEMBERSHIP_KIND_COLOR_DARK: Record<string, string> = {
  SALARIE: "#64b5f6",   // blue 300
  LIBERAL: "#ce93d8",   // purple 200
  VACATAIRE: "#ffb74d", // orange 300
  STAGIAIRE: "#bdbdbd", // gray 400
  RESERVISTE: "#81c784", // green 300
};

function membershipColor(kind: string | null | undefined, mode: "light" | "dark"): string | undefined {
  if (!kind) return undefined;
  const map = mode === "dark" ? MEMBERSHIP_KIND_COLOR_DARK : MEMBERSHIP_KIND_COLOR_LIGHT;
  return map[kind];
}


function kindBg(color: string | null, mode: "light" | "dark", strength: "strong" | "soft" = "strong"): string | undefined {
  if (!color) return undefined;
  if (mode === "dark") {
    // Need real contrast against #121212 — push the alpha higher than before.
    return alpha(color, strength === "strong" ? 0.7 : 0.45);
  }
  return strength === "strong" ? color : alpha(color, 0.6);
}

function kindBorder(color: string | null, mode: "light" | "dark"): string | undefined {
  if (!color) return undefined;
  return mode === "dark" ? alpha(color, 0.9) : alpha(color, 0.7);
}

export function RosterPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [from, setFrom] = useState(todayIso());
  // A phone shows one day at a time; the wider spans are a desktop-grid idea.
  const [span, setSpan] = useState(() =>
    window.matchMedia("(max-width: 899px)").matches ? 1 : 2,
  );
  const [highlightPracId, setHighlightPracId] = useState<string | null>(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const [pracQuery, setPracQuery] = useState("");
  const [kindFilter, setKindFilter] = useState<Set<string>>(new Set());

  const to = useMemo(() => isoPlus(from, span - 1), [from, span]);

  const profile = useQuery({
    queryKey: ["profile-roster"],
    queryFn: getProfile,
  });
  const myPracId = profile.data?.practitioner?.id ?? null;

  const pracCandidates = useQuery({
    queryKey: ["roster-practitioner-search", pracQuery],
    queryFn: () =>
      listPractitioners({ q: pracQuery || undefined, status: "ACTIVE", limit: 30 }),
    enabled: pracQuery.length >= 2,
  });

  const roster = useQuery({
    queryKey: ["roster", from, to],
    queryFn: () => getRoster(from, to),
    enabled: Boolean(from && to),
  });

  const effectiveHighlight = onlyMine ? myPracId : highlightPracId;

  const filteredDays = useMemo(() => {
    if (kindFilter.size === 0) return roster.data?.days ?? [];
    return (roster.data?.days ?? []).map((d) => ({
      ...d,
      cells: d.cells.filter((c) => kindFilter.has(c.slot_kind)),
    }));
  }, [roster.data, kindFilter]);

  // Group orgs by kind, in preferred order
  const groupsByKind = useMemo(() => {
    const m = new Map<OrganizationKind, RosterGroup[]>();
    for (const g of roster.data?.groups ?? []) {
      const arr = m.get(g.kind) ?? [];
      arr.push(g);
      m.set(g.kind, arr);
    }
    const ordered: { kind: OrganizationKind; orgs: RosterGroup[] }[] = [];
    for (const k of KIND_ORDER) {
      const arr = m.get(k);
      if (arr && arr.length) ordered.push({ kind: k, orgs: arr });
    }
    return ordered;
  }, [roster.data]);

  const shiftFrom = (delta: number) => setFrom(isoPlus(from, delta));

  return (
    <Box>
      <PageHeader
        title={t("roster.title")}
        subtitle={t("roster.subtitle")}
        helpTerm="roster"
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={async () => {
                try {
                  const blob = await downloadRosterCsv(from, to);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `roster-${from}_${to}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  alert((e as Error).message);
                }
              }}
            >
              {t("roster.exportCsv")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              {t("roster.print")}
            </Button>
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => shiftFrom(-span)} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <TextField
          size="small"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          label={t("roster.from")}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          size="small"
          value={span}
          onChange={(e) => setSpan(Number(e.target.value))}
          label={t("roster.span")}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value={1}>1 {t("roster.day")}</MenuItem>
          <MenuItem value={2}>2 {t("roster.days")}</MenuItem>
          <MenuItem value={7}>7 {t("roster.days")}</MenuItem>
        </TextField>
        <IconButton onClick={() => shiftFrom(+span)} size="small">
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {from} → {to}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={pracCandidates.data?.items ?? []}
          getOptionLabel={(o) =>
            o.display_name + (o.rpps ? ` · ${o.rpps}` : "")
          }
          filterOptions={(x) => x}
          loading={pracCandidates.isFetching}
          disabled={onlyMine}
          onInputChange={(_, v) => setPracQuery(v)}
          onChange={(_, v) => setHighlightPracId(v?.id ?? null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("roster.highlightPractitioner")}
              placeholder={t("roster.searchPlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <PersonSearchIcon fontSize="small" sx={{ mr: 0.5, color: "action.active" }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {myPracId && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
              />
            }
            label={t("roster.onlyMine")}
          />
        )}
        {effectiveHighlight && (
          <Chip
            size="small"
            color="primary"
            label={t("roster.highlighting")}
            onDelete={() => {
              setHighlightPracId(null);
              setOnlyMine(false);
            }}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {t("roster.filterKind")}:
        </Typography>
        {(["GARDE", "DEMI_GARDE", "POSTE", "ASTREINTE"] as const).map((k) => {
          const selected = kindFilter.has(k);
          return (
            <Chip
              key={k}
              size="small"
              label={t(`slotKind.${k}`)}
              color={selected ? "primary" : "default"}
              variant={selected ? "filled" : "outlined"}
              onClick={() => {
                const next = new Set(kindFilter);
                if (next.has(k)) next.delete(k);
                else next.add(k);
                setKindFilter(next);
              }}
            />
          );
        })}
        {kindFilter.size > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={t("common.clear")}
            onClick={() => setKindFilter(new Set())}
          />
        )}
      </Stack>

      {roster.isLoading && <CircularProgress size={20} />}

      {roster.data && roster.data.groups.length === 0 && (
        <Alert severity="info">{t("roster.noOrgs")}</Alert>
      )}

      {/* Phone: one vertical card list per day. The orgs × days matrix below
          is unreadable under ~900 px, so it is not merely restyled — it is
          replaced by a different rendering of the same data. */}
      {isMobile && roster.data && roster.data.groups.length > 0 && (
        <Stack spacing={2}>
          {filteredDays.map((d) => (
            <Box key={d.date}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  position: "sticky",
                  top: 56,
                  zIndex: 1,
                  bgcolor: "background.default",
                  py: 0.5,
                }}
              >
                {formatDow(d.date, i18n.language)}
              </Typography>
              <RosterMobileDay
                date={d.date}
                groups={roster.data!.groups}
                cells={d.cells}
                highlightId={effectiveHighlight}
                onlyHighlighted={onlyMine}
                isToday={d.date === todayIso()}
              />
            </Box>
          ))}
        </Stack>
      )}

      {!isMobile && roster.data && roster.data.groups.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ overflowX: "auto" }}>
              <Box
                component="table"
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: 12,
                  "& th, & td": {
                    border: 1,
                    borderColor: "divider",
                    padding: "4px 6px",
                    verticalAlign: "top",
                    minWidth: 90,
                  },
                  "& th.dateCell": {
                    minWidth: 100,
                    fontWeight: 700,
                    textAlign: "center",
                  },
                }}
              >
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" scope="col" aria-label={t("roster.date")} />
                    {groupsByKind.map((g) => (
                      <Box
                        component="th"
                        scope="colgroup"
                        key={g.kind}
                        colSpan={g.orgs.length}
                        sx={{
                          backgroundColor: kindBg(g.orgs[0]?.color ?? null, mode, "strong"),
                          color: mode === "dark" ? "common.white" : "text.primary",
                          textAlign: "center",
                          fontWeight: 700,
                        }}
                      >
                        {g.kind}
                      </Box>
                    ))}
                  </Box>
                  <Box component="tr">
                    <Box component="th" scope="col" aria-label={t("roster.date")} />
                    {groupsByKind.flatMap((g) =>
                      g.orgs.map((org) => (
                        <Box
                          component="th"
                          scope="col"
                          key={org.id}
                          sx={{
                            backgroundColor: kindBg(org.color, mode, "soft"),
                            color: mode === "dark" ? "common.white" : "text.primary",
                            textAlign: "center",
                          }}
                          title={`${org.code} · ${org.name}`}
                        >
                          {org.name}
                        </Box>
                      )),
                    )}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredDays.map((d) => (
                    <DayRows
                      key={d.date}
                      date={d.date}
                      cells={d.cells}
                      groups={groupsByKind}
                      locale={i18n.language}
                      mode={mode}
                      highlightId={effectiveHighlight}
                      onlyHighlighted={onlyMine}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {roster.data && roster.data.groups.length > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ mt: 1.5, flexWrap: "wrap" }}
          alignItems="center"
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary">
            {t("roster.legend")}:
          </Typography>
          {Object.keys(MEMBERSHIP_KIND_COLOR_LIGHT).map((kind) => (
            <Stack key={kind} direction="row" alignItems="center" spacing={0.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: membershipColor(kind, mode),
                }}
              />
              <Typography variant="caption">
                {t(`membershipKind.${kind}`, { defaultValue: kind })}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}


function DayRows({
  date,
  cells,
  groups,
  locale,
  mode,
  highlightId,
  onlyHighlighted,
}: {
  date: string;
  cells: RosterCell[];
  groups: { kind: OrganizationKind; orgs: RosterGroup[] }[];
  locale: string;
  mode: "light" | "dark";
  highlightId: string | null;
  onlyHighlighted: boolean;
}) {
  // Split into "jour" and "garde" by label heuristic
  const day: RosterCell[] = [];
  const garde: RosterCell[] = [];
  for (const c of cells) {
    if (isGardeLabel(c.slot_label)) garde.push(c);
    else day.push(c);
  }

  const orderedOrgs = groups.flatMap((g) => g.orgs);
  const dow = new Date(date).getDay();
  const isWeekend = dow === 0 || dow === 6;

  const weekendBg = mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
  const weekendHeaderBg =
    mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)";
  const rowSx = isWeekend ? { backgroundColor: weekendBg } : undefined;
  const headerSx = {
    minWidth: 100,
    fontWeight: 700,
    textAlign: "center" as const,
    backgroundColor: isWeekend ? weekendHeaderBg : undefined,
  };

  const vacantCount = cells.filter((c) => !c.practitioner_id).length;

  return (
    <>
      <Box component="tr" sx={rowSx}>
        <Box
          component="th"
          scope="row"
          rowSpan={2}
          className="dateCell"
          sx={headerSx}
        >
          <Box>{formatDow(date, locale)}</Box>
          {vacantCount > 0 && (
            <Chip
              size="small"
              color="error"
              label={vacantCount}
              sx={{
                height: 18,
                fontSize: 10,
                mt: 0.25,
                "& .MuiChip-label": { px: 0.75, fontWeight: 700 },
              }}
            />
          )}
        </Box>
        {orderedOrgs.map((org, idx) => (
          <OrgCell
            key={`d-${org.id}-${idx}`}
            org={org}
            cells={day}
            date={date}
            mode={mode}
            highlightId={highlightId}
            onlyHighlighted={onlyHighlighted}
          />
        ))}
      </Box>
      <Box component="tr" sx={rowSx}>
        {orderedOrgs.map((org, idx) => (
          <OrgCell
            key={`g-${org.id}-${idx}`}
            org={org}
            cells={garde}
            date={date}
            mode={mode}
            highlightId={highlightId}
            onlyHighlighted={onlyHighlighted}
          />
        ))}
      </Box>
    </>
  );
}


function OrgCell({
  org,
  cells,
  date,
  mode,
  highlightId,
  onlyHighlighted,
}: {
  org: RosterGroup;
  cells: RosterCell[];
  date: string;
  mode: "light" | "dark";
  highlightId: string | null;
  onlyHighlighted: boolean;
}) {
  const orgConf = rosterGroupToOrg(org);
  // For the date this cell renders, drop non-operating slots so a SAS that
  // closes 20h-8h doesn't show "À pourvoir" rows during the night, and an
  // organization that doesn't work weekends shows blank on Sat/Sun.
  const slots = cells.filter(
    (c) =>
      c.organization_id === org.id &&
      slotIsOperating(orgConf, date, { start_time: c.start_time, end_time: c.end_time }),
  );
  if (slots.length === 0) return <Box component="td" />;

  // Build runs: per-practitioner consecutive-slot blocks (and vacant blocks
  // listed per slot). Two slots are "consecutive" when the end_time of one
  // equals the start_time of the next.
  const byPid = new Map<string, RosterCell[]>();
  for (const c of slots) {
    const key = c.practitioner_id ?? "__vacant__";
    const arr = byPid.get(key) ?? [];
    arr.push(c);
    byPid.set(key, arr);
  }

  type Run = { cells: RosterCell[] };
  const runs: Run[] = [];
  for (const list of byPid.values()) {
    list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    // Vacant cells stay isolated (one run per slot) so each "À pourvoir"
    // line refers to a specific tranche.
    if (list[0]!.practitioner_id == null) {
      for (const c of list) runs.push({ cells: [c] });
      continue;
    }
    let current: RosterCell[] = [];
    for (const c of list) {
      if (current.length === 0 || current[current.length - 1]!.end_time === c.start_time) {
        current.push(c);
      } else {
        runs.push({ cells: current });
        current = [c];
      }
    }
    if (current.length > 0) runs.push({ cells: current });
  }
  // Sort runs by start_time for stable visual order.
  runs.sort((a, b) => a.cells[0]!.start_time.localeCompare(b.cells[0]!.start_time));

  return (
    <Box component="td" sx={{ p: 0.5 }}>
      <Stack spacing={0.5}>
        {runs.map((r, i) => (
          <SlotBlock
            key={i}
            cells={r.cells}
            orgColor={org.color}
            mode={mode}
            highlightId={highlightId}
            onlyHighlighted={onlyHighlighted}
          />
        ))}
      </Stack>
    </Box>
  );
}


function SlotBlock({
  cells,
  orgColor,
  mode,
  highlightId,
  onlyHighlighted,
}: {
  cells: RosterCell[];
  orgColor: string | null;
  mode: "light" | "dark";
  highlightId: string | null;
  onlyHighlighted: boolean;
}) {
  const { t } = useTranslation();
  const first = cells[0]!;
  const last = cells[cells.length - 1]!;
  const isFused = cells.length > 1;
  const label = isFused
    ? `${first.start_time.slice(0, 2)}h–${last.end_time.slice(0, 2)}h`
    : first.slot_label.trim();
  const timeRange = `${first.start_time.slice(0, 5)}–${last.end_time.slice(0, 5)}`;
  // For fused runs the target chip is meaningless (per-slot target may vary)
  // so we only render it for single-slot blocks.
  const target = isFused ? 0 : first.target_headcount ?? 1;
  const assignedCount = first.practitioner_id ? 1 : 0;
  const understaffed = assignedCount < target;
  // Each run is now per-practitioner so highlight applies to the whole run.
  const hasHighlight = first.practitioner_id === highlightId;
  const allDimmed =
    onlyHighlighted && highlightId
      ? first.practitioner_id !== highlightId
      : false;

  const blockBg =
    mode === "dark"
      ? orgColor
        ? alpha(orgColor, 0.22)
        : "rgba(255,255,255,0.08)"
      : orgColor
        ? alpha(orgColor, 0.18)
        : "rgba(0,0,0,0.02)";
  const blockBorder = kindBorder(orgColor, mode) ?? "divider";

  return (
    <Tooltip title={`${label} · ${timeRange}`}>
      <Box
        sx={{
          opacity: allDimmed ? 0.25 : 1,
          backgroundColor: hasHighlight ? "warning.light" : blockBg,
          border: hasHighlight ? "2px solid" : "1px solid",
          borderColor: hasHighlight ? "warning.main" : blockBorder,
          borderRadius: 0.75,
          px: 0.75,
          py: 0.5,
          transition: "background-color 120ms, opacity 120ms",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              color: "text.primary",
            }}
          >
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
            {timeRange}
          </Typography>
          <SlotKindChip kind={first.slot_kind} />
          {target > 1 && (
            <Tooltip
              title={t("roster.staffingTooltip", {
                assigned: assignedCount,
                target,
              })}
            >
              <Chip
                size="small"
                color={understaffed ? "error" : "success"}
                variant={understaffed ? "filled" : "outlined"}
                label={`${assignedCount}/${target}`}
                sx={{
                  height: 16,
                  fontSize: 9,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            </Tooltip>
          )}
        </Stack>
        <Stack spacing={0.25} sx={{ mt: 0.25 }}>
          {/* For a fused run, all cells share the same practitioner → render once */}
          {[first].map((c, i) => {
            const isHighlighted = highlightId && c.practitioner_id === highlightId;
            const memberColor = membershipColor(c.membership_kind, mode);
            const tooltipParts: string[] = [];
            if (c.practitioner_name) tooltipParts.push(c.practitioner_name);
            if (c.membership_kind) {
              tooltipParts.push(
                t(`membershipKind.${c.membership_kind}`, {
                  defaultValue: c.membership_kind,
                }),
              );
            }
            if (c.fte_percent != null && c.fte_percent < 100) {
              tooltipParts.push(`ETP ${c.fte_percent}%`);
            }
            const nameNode = (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                key={`${c.practitioner_id ?? "vacant"}-${i}`}
                sx={{ lineHeight: 1.25 }}
              >
                {memberColor && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: memberColor,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isHighlighted ? 700 : 500,
                    fontStyle: c.practitioner_id ? "normal" : "italic",
                    color: c.practitioner_id
                      ? isHighlighted
                        ? mode === "dark"
                          ? "warning.light"
                          : "warning.dark"
                        : memberColor ?? "text.primary"
                      : mode === "dark"
                        ? "#ff8a80"
                        : "error.main",
                    flex: 1,
                  }}
                >
                  {c.practitioner_name ?? t("roster.toFill")}
                </Typography>
                {c.fte_percent != null && c.fte_percent < 100 && (
                  <Chip
                    size="small"
                    label={`${c.fte_percent}%`}
                    sx={{
                      height: 14,
                      fontSize: 9,
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
            );
            return tooltipParts.length > 1 ? (
              <Tooltip
                key={`${c.practitioner_id ?? "vacant"}-${i}-t`}
                title={tooltipParts.join(" · ")}
              >
                {nameNode}
              </Tooltip>
            ) : (
              nameNode
            );
          })}
        </Stack>
      </Box>
    </Tooltip>
  );
}
